// Per-type extension-field render schemas. Keyed by asset_type.key.
// Must match the extension tables in firefleet-api/sql-scripts/phase-1a-asset-management.sql
// and the EXTENSION_FIELD_WHITELIST in firefleet-api/src/assets/assets-service.js.
//
// These schemas drive ONLY the initial-create form in NewAssetForm. The AssetDetail
// extension card renders its own per-type block (see ExtensionRows in AssetDetail.tsx)
// because some fields are read-only (sequence_number) and the create vs detail surfaces
// have diverged enough that sharing the schema produced more friction than it saved.
//
// Spine-level Notes (assets.notes) is the single notes column for every asset; we
// intentionally do not surface a per-extension notes field here.

import type { BuildPhase } from './types';

export type ExtensionInputType = 'text' | 'number' | 'select' | 'checkbox';

export interface ExtensionFieldDef {
  key: string;
  label: string;
  inputType?: ExtensionInputType;
  placeholder?: string;
  mono?: boolean;
  options?: Array<{ value: string; label: string }>;
  helpText?: string;
  // When true, the field is auto-computed at create and should NOT appear in the initial
  // form; it's set via the auto-naming path and shown/edited later on AssetDetail.
  autoComputed?: boolean;
  // For boolean inputs: the value to send (as a string in the form state) when the box
  // is checked vs unchecked. Defaults to 'true' / 'false'.
  defaultChecked?: boolean;
}

const BUILD_PHASE_OPTIONS: Array<{ value: BuildPhase; label: string }> = [
  { value: 'up_next',       label: 'Up Next' },
  { value: 'sd_card_ready', label: 'SD Card Ready' },
  { value: 'default_image', label: 'Default Image' },
  { value: 'working',       label: 'Working' },
  { value: 'in_service',    label: 'In Service' },
];

export const EXTENSION_FIELD_SCHEMAS: Record<string, ExtensionFieldDef[]> = {
  smart_charger: [
    // vpn_ip + sequence_number + display_code are auto-computed at create.
    // firmware_version + hardware_rev columns still exist in the DB but are intentionally
    // not surfaced anywhere on the FE for now (operator preference); revisit if a future
    // workflow needs them.
    { key: 'charger_power', label: 'Power', inputType: 'select',
      options: [{ value: 'regular', label: 'Regular' }, { value: 'super', label: 'Super' }],
      helpText: 'Shop-floor designation for the charger.' },
    { key: 'build_phase', label: 'Build Phase', inputType: 'select',
      options: BUILD_PHASE_OPTIONS,
      helpText: 'Advances the charger through assembly. "In Service" flips Status to Ready.' },
  ],
  time_machine: [
    { key: 'build_phase', label: 'Build Phase', inputType: 'select',
      options: BUILD_PHASE_OPTIONS,
      helpText: '"In Service" flips Status to Ready.' },
    { key: 'on_latest_firmware', label: 'On Latest Firmware', inputType: 'checkbox',
      defaultChecked: true,
      helpText: 'Uncheck if this unit needs the firmware update.' },
  ],
  rtk_unit: [
    { key: 'on_latest_firmware', label: 'On Latest Firmware', inputType: 'checkbox',
      defaultChecked: true,
      helpText: 'Uncheck if this unit needs the firmware update.' },
  ],
};
